import type { TimesheetDay } from '@/api/types-bridge'

type DayLike = Pick<
  TimesheetDay,
  'status' | 'submittedAt' | 'approvedAt' | 'rejectedAt' | 'synthesized'
>

/**
 * True when a past empty day's terminal status is a server-made display
 * hint rather than a submission anyone made. Such a day does NOT lock.
 *
 * The BE flags it explicitly. A BE that predates the flag gets the old
 * all-null-timestamps heuristic, which is right for weekdays but also
 * matches the system-approved weekend rows that really do lock.
 */
export function isSynthesizedDay(day: DayLike): boolean {
  if (day.synthesized !== undefined) return day.synthesized
  return day.status !== 'pending' && !day.submittedAt && !day.approvedAt && !day.rejectedAt
}

/** Submitted / approved days refuse new, edited and deleted entries. */
export function isDayLocked(day: DayLike | null | undefined): boolean {
  if (!day) return false
  if (day.status !== 'submitted' && day.status !== 'approved') return false
  return !isSynthesizedDay(day)
}

export type DayDisplayStatus = 'pending' | 'submitted' | 'approved' | 'rejected'

/**
 * What the day header badge shows. A synthesized day shows nothing: no one
 * submitted it, and a "Submitted" badge would read as a lock it isn't.
 */
export function dayDisplayStatus(day: DayLike | null | undefined): DayDisplayStatus {
  if (!day) return 'pending'
  if (isDayLocked(day)) return day.status as 'submitted' | 'approved'
  if (day.status === 'pending' && day.rejectedAt) return 'rejected'
  return 'pending'
}
